
import { v4 as uuidv4 } from "uuid";
import { DisablableOption, ModelBase, UpsertableBase } from "@models";
import { RollPlus, RollPlusUpgradable, Spell, SpellsFromListWithCosts } from "@dndModels";
import { AbilityScoreType, DataType } from "@dndModels/Types";

/**
 * Matches all common dash/hyphen Unicode variants in sequence,
 * replacing them with a single standard hyphen-minus (U+002D).
 *
 * Covers: hyphen-minus, various Unicode dashes (en, em, figure, etc.),
 * Armenian hyphen, Hebrew punctuation, Mongolian todo soft hyphen,
 * and fullwidth hyphen-minus.
 */
const DASH_VARIANTS = /[\u002D\u058A\u05BE\u1400\u1806\u2010\u2011\u2012\u2013\u2014\u2015\u2E17\u2E1A\u2E3A\u2E3B\u2E40\u301C\u3030\uFE31\uFE32\uFE58\uFE63\uFF0D\u2212]+/g;

export class CommonUtilitiesClass {
  unique_array<T extends string | number>(arr: T[]): T[] {
    const unique = (value: T, index: number, self: T[]) => {
      return self.indexOf(value) === index;
    };
    
    return arr.filter(unique);
  }

  extractListTypes(input: string) {
    const results: string[] = [];
    const pattern = /\{list_of_type:([^;]*);/g;
    
    let match: any = null;
    while ((match = pattern.exec(input)) !== null) {
      const r = match[1];
      results.push(...r.split(","));
    }
    
    return results.filter(o => o.length > 0) as DataType[];
  }

  capitalize_first(fix_me: string, only = false, except: string[] = []) {
    if (except.length > 0) {
      if (except.some(str => this.cleanCompare(str, fix_me))) {
        return only ? fix_me.toLocaleLowerCase() : fix_me;
      }
    }
    let return_me = "";
    if (fix_me.length > 0) {
      return_me = fix_me[0].toUpperCase();
      return_me += only ? fix_me.substring(1).toLowerCase() : fix_me.substring(1);
    }
    return return_me;
  }
  
  capitalize_firsts(fix_me: string, only = false, except: string[] = []) {
    let return_me = "";
    if (fix_me) {
      const fix_us = fix_me.split(" ");
      for (const fm of fix_us) {
        if (return_me.length > 0) {
          return_me += " ";
        }
        return_me += this.capitalize_first(fm, only, except);
      }
    }
    return return_me.trim();
  }

  // Takes a string of what should be a number, and fixes it.
  // The reason for this is sometimes we are given O instead of 0 or S instead of 5.
  // It also removes unnecessary + signs.
  // It then returns it as a number
  fix_number_string(fix_me: string) {
    let return_me = "";
    for (let i = 0; i < fix_me.length; ++i) {
      const c = fix_me[i];
      if (c === "S") {
        return_me += "5";
      } else if (["O","o"].includes(c)) {
        return_me += "0";
      } else if (["L","l","I"].includes(c)) {
        return_me += "1";
      } else if (!["+",","," ","(",")","{", "}", "[", "]", "!", "'", '"'].includes(c)) {
        return_me += c;
      }
    }
    if (return_me.includes("/")) {
      const pieces = return_me.split("/");
      if (pieces.length === 2) {
        return +pieces[0] / +pieces[1];
      } else {
        return 0;
      }
    } else {
      return +return_me;
    }
  }
  // Removes any non-numeric characters and returns the string as a number
  clean_number_string(fix_me: string) {
    let return_me = "";
    for (let i = 0; i < fix_me.length; ++i) {
      const c = fix_me[i];
      if (["0","1","2","3","4","5", "6", "7", "8", "9","."].includes(c)) {
        return_me += c;
      }
    }
    if (return_me.length === 0) {
      return 0;
    } else {
      return +return_me;
    }
  }

  addPlusMaybe(add_to_me: string | number, blank_on_0: (boolean | null) = null, log_it = false): string {
    if (log_it) {
      console.log(add_to_me);
    }
    if (typeof add_to_me === "string") {
      if (add_to_me.length > 0) {
        if (add_to_me[0] === "0") {
          if (blank_on_0 !== true) {
            return `+${add_to_me}`;
          } else {
            return `${add_to_me}`;
          }
        } else if (add_to_me[0] !== "-") {
          return `+${add_to_me}`;
        }
      }
      return add_to_me;
    } else {
      if (add_to_me === 0) {
        if (blank_on_0 === true) {
          return "";
        } else if (blank_on_0 === false) {
          return "0";
        } else {
          return "+0";
        }
      } else if (add_to_me > 0) {
        return `+${add_to_me}`;
      }
      return `${add_to_me}`;
    }
  }

  addPlusMaybe2Strings(first: string, second: string | number, reverse = false): string {
    if (reverse) {
      if (typeof second === "number") {
        if (second > 0) {
          return `+${second}${first}`;
        } else if (second < 0) {
          return `${second}${first}`;
        } else if (first === "") {
          return "+0";
        } else {
          return first;
        }
      } else {
        if (first.length > 0) {
          if (first[0] === "-") {
            return `${second}${first}`;
          } else {
            return `${second}+${first}`;
          }
        } else {
          return `${second}`;
        }
      }
    } else {
      if (first.length > 0) {
        if (first[first.length - 1] === ")") {
          return `${first} ${second}`;
        } else if ((typeof second === "number" && second < 0) || (typeof second === "string" && second.length > 0 && second[0] === "-")) {
          return `${first}${second}`;
        } else if (second === 0 || second === "0") {
          return first;
        } else {
          return `${first}+${second}`;
        }
      } else {
        return `${second}`;
      }
    }
  }

  sum(nums: number[]) {
    return nums.reduce((accumulator: number, num: number) => {
      return accumulator + num;
    }, 0);
  }

  ability_score_abbreviation(score: string) {
    let abbreviation: AbilityScoreType = "STR";
    switch(score.toLowerCase().trim()) {
      case "str":
        abbreviation = "STR";
      break;
      case "strength":
        abbreviation = "STR";
      break;
      case "dex":
        abbreviation = "DEX";
      break;
      case "dexterity":
        abbreviation = "DEX";
      break;
      case "con":
        abbreviation = "CON";
      break;
      case "constitution":
        abbreviation = "CON";
      break;
      case "int":
        abbreviation = "INT";
      break;
      case "intelligence":
        abbreviation = "INT";
      break;
      case "wis":
        abbreviation = "WIS";
      break;
      case "wisdom":
        abbreviation = "WIS";
      break;
      case "cha":
        abbreviation = "CHA";
      break;
      case "charisma":
        abbreviation = "CHA";
      break;
    }
    return abbreviation;
  }

  ability_score_full(abbreviation: string): string {
    let score = "";
    switch(abbreviation) {
      case "STR":
        score = "Strength";
      break;
      case "Strength":
        score = "Strength";
      break;
      case "DEX":
        score = "Dexterity";
      break;
      case "Dexterity":
        score = "Dexterity";
      break;
      case "CON":
        score = "Constitution";
      break;
      case "Constitution":
        score = "Constitution";
      break;
      case "INT":
        score = "Intelligence";
      break;
      case "Intelligence":
        score = "Intelligence";
      break;
      case "WIS":
        score = "Wisdom";
      break;
      case "Wisdom":
        score = "Wisdom";
      break;
      case "CHA":
        score = "Charisma";
      break;
      case "Charisma":
        score = "Charisma";
      break;
    }
    return score;
  }

  clean_for_parse(clean_me: string): string {
    let cleaned = this.replaceAll(clean_me, ",", "");
    cleaned = this.replaceAll(cleaned, ".", "");
    cleaned = this.replaceAll(cleaned, ";", "");
    cleaned = this.replaceAll(cleaned, ":", "");
    cleaned = this.replaceAll(cleaned, "(", " ");
    cleaned = this.replaceAll(cleaned, ")", " ");
    cleaned = this.replaceAll(cleaned, "{", " ");
    cleaned = this.replaceAll(cleaned, "}", " ");
    cleaned = this.replaceAll(cleaned, "[", " ");
    cleaned = this.replaceAll(cleaned, "]", " ");
    cleaned = this.replaceAll(cleaned, "!", "");
    cleaned = this.replaceAll(cleaned, "'", "");
    cleaned = this.replaceAll(cleaned, '"', "");
    cleaned = this.replaceAll(cleaned, "·", "");

    return cleaned.toLowerCase();
  }

  replaceAll(cleanMe: string, replaceMe: string, withMe: string) {
    let dirtyString = cleanMe;
    let newString = "";
    while (dirtyString.indexOf(replaceMe) > -1) {
      newString += dirtyString.substring(0, dirtyString.indexOf(replaceMe)) + withMe;
      dirtyString = dirtyString.substring(dirtyString.indexOf(replaceMe) + replaceMe.length);
    }
    newString += dirtyString;
    return newString;
  }

  shuffleList(list: any[]) {
    const old_list = [...list];
    const new_list: any[] = [];
    // Recursively take random indexes from the current size of old_card_ids and splice it from it, then push it to new_card_ids
    while (old_list.length > 0) {
      const random_index = this.get_random(old_list.length) - 1;
      const obj = old_list.splice(random_index, 1);
      new_list.push(obj[0]);
    }
    return new_list;
  }

  findOverlap(arr1: string[], arr2: string[]) {
    for (let i = 0; i < arr1.length; ++i) {
      if (arr2.includes(arr1[i])) {
        return arr1[i];
      }
    }
    return "";
  }

  anyOverlaps(arr1: string[], arr2: string[]) {
    for (let i = 0; i < arr1.length; ++i) {
      if (arr2.includes(arr1[i])) {
        return true;
      }
    }
    return false;
  }

  anyIncludes(str: string | number, arr: (string | number)[]) {
    if (arr.length > 0) {
      if (typeof arr[0] === "string" && typeof str === "string") {
        const strs = [...arr] as string[];
        for (let i = 0; i < strs.length; ++i) {
          try {
            if (strs[i].includes(str)) {
              return true;
            }
          } catch (ex) {
            console.error("any_includes error:")
            console.error(ex);
            return false;  
          }
        }
      } else if (typeof arr[0] === "number" && typeof str === "number") {
        const strs = [...arr] as number[];
        return strs.includes(str);
      }
    }
    return false;
  }

  anyIncludes2(arr1: (string | number)[], arr2: (string | number)[], log_it = false) {
    if (log_it) {
      debugger;
    }
    for (let i = 0; i < arr1.length; ++i) {
      if (this.anyIncludes(arr1[i], arr2)) {
        return true;
      }
    }
    return false;
  }

  findOverlaps(arr1: string[], arr2: string[]) {
    const overlaps: string[] = [];
    for (let i = 0; i < arr1.length; ++i) {
      if (arr2.includes(arr1[i])) {
        overlaps.push(arr1[i]);
      }
    }
    return overlaps;
  }

  /**
   * This returns true if there are any overlaps in the two number ranges.
   * resets is an optional boolean that is for use in situations where the possible numbers have a limit, 
   * and once it reaches that it starts back at the beginning.
   * Example is a calendar. The last day of the year is followed by the start of the next year, so 1231 is followed by 101.
   * If resets is true then ranges can have a start which is a higher number than its end, 
   * because the range goes to the end and then from the start of the cycle to the end of the range (like winter).
   * 
   * true_on_kiss makes it so they are considered to be overlapping if the start of one is the same as the end of the other.
   */
  doNumRangesOverlap({ range1, range2, resets = false, true_on_kiss = false }: 
    { 
      range1: { start: number, end: number }, 
      range2: { start: number, end: number }, 
      resets?: boolean,
      true_on_kiss?: boolean
    }
  ) {
    if (range1.start === range2.start || range1.end === range2.end) {
      return true;
    }
    if (true_on_kiss && (range1.end === range2.start || range2.end === range1.start)) {
      return true;
    }
    
    if (!resets || (range1.start <= range1.end && range2.start <= range2.end)) {
      // Simple check. No reset or neither range resets.
      if (range1.start < range2.start) {
        if (range2.start <= range1.end) {
          return true;
        }
      } else {
        if (range1.start <= range2.end) {
          return true;
        }
      }
    } else {
      if (range1.start > range1.end) {
        // range1 resets
        if (range2.start > range2.end) {
          return true; // Both reset
        }
        if (range1.start < range2.start || // range2 starts after range1, and range1 resets, so it goes to the end, so they overlap.
          range2.start < range1.end) { // range2 starts before the end of range1, and range1 resets, so it starts before the beginning, so they overlap.
          return true;
        } else if (range2.start < range1.start && range1.start < range2.end) {
          return true; // The end of the non-resetting one overlaps with the resetting one before the reset.
        }
      } else {
        // We already know at least one resets, so if range1 doesn't, then range2 does
        if (range2.start < range1.start || range1.start < range2.end) { // Same logic as above for the 1 overlap possibility, just reversed
          return true;
        } else if (range1.start < range2.start && range2.start < range1.end) {
          return true; // The end of the non-resetting one overlaps with the resetting one before the reset.
        }
      }
    } 
    return false;
  }
  numInRange({ range, num, resets = false }: 
    { 
      range: { start: number, end: number }, 
      num: number, 
      resets?: boolean
    }
  ) {
    if (range.start === num || range.end === num) {
      return true;
    }
    
    if (!resets || (range.start <= range.end)) {
      // Simple check. No reset 
      if (range.start < num && num < range.end) {
        return true;
      }
    } else {
      // range resets
      if (range.start < num || num < range.end) { 
        return true;
      }
    } 
    return false;
  }

  findOneInString(str: string, arr: string[], starts_with = false) {
    for (let i = 0; i < arr.length; ++i) {
      if (starts_with) {
        if (str.toLowerCase().startsWith(arr[i].toLowerCase())) {
          return arr[i];
        }
      } else {
        if (str.toLowerCase().includes(arr[i].toLowerCase())) {
          return arr[i];
        }
      }
    }
    return "";
  }

  inListIgnoreCase(str: string, arr: string[]) {
    str = str.toLowerCase().trim();
    for (let i = 0; i < arr.length; ++i) {
      if (arr[i].toLowerCase().trim() === str) {
        return arr[i];
      }
    }
    return "";
  }

  replaceInList(list: any[], replace_id: string, replace_with: any, id_field: string): any[] {
    const new_list: any[] = [];
    list.forEach((item: any) => {
      if (item[id_field] === replace_id) {
        new_list.push(replace_with);
      } else {
        new_list.push(item);
      }
    });
    return new_list;
  }

  listsMatch(list1: string[] | DisablableOption[], list2: string[] | DisablableOption[]) {
    if (list1.length === list2.length) {
      if (list1.length === 0) {
        return true;
      } else {
        if (typeof list1[0] !== typeof list2[0]) {
          return false;
        } else if (typeof list1[0] === "string") {
          const strs1 = list1 as string[];
          const strs2 = list2 as string[];
          for (let i = 0; i < strs1.length; ++i) {
            if (!strs2.includes(strs1[i])) {
              return false;
            }
          }
          return true;
        } else {
          try {
            const dos1 = list1 as DisablableOption[];
            const dos2 = list2 as DisablableOption[];
            for (let i = 0; i < dos1.length; ++i) {
              const doo = dos1[i];
              if (dos2.filter(o => o.id === doo.id && o.text === doo.text).length === 0) {
                return false;
              }
            }
            return true;
          } catch (ex) {
            console.error(ex);
            debugger;
          }
        }
      }
    }
    return false;
  }

  findListMismatches(list1: string[], list2: string[]): string[] {
    const m1 = list1.filter(o => !list2.includes(o));
    const m2 = list2.filter(o => !list1.includes(o));
    return [...m1,...m2];
  }

  findMismatch(obj1: any, obj2: any, ignore_fields: string[] = []): string {
    if (obj1 === null || obj1 === undefined || obj2 === null || obj2 === undefined) {
      if ((obj1 === null || obj1 === undefined) && (obj2 === null || obj2 === undefined)) {
        return "";
      } else {
        return "Null difference";
      }
    }
    try {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      let listMismatches = this.findListMismatches(keys1, keys2);
      let listsMatch = listMismatches.length === 0;
      if (!listsMatch) {
        // Check if matches other than empty
        listsMatch = true;
        for (const key of listMismatches) {
          if ((obj1[key] !== null && obj1[key] !== undefined) || (obj2[key] !== null && obj2[key] !== undefined)) {
            listsMatch = false;
            break;
          }
        }
      }
      if (listsMatch) {
        for (const key of keys1) {
          if (!ignore_fields.includes(key)) {
            const val1 = obj1[key];
            const val2 = obj2[key];
            if (typeof(val1) !== typeof(val2)) {
              return `Type mismatch ${val1} !== ${val2}`;
            } else {
              if (typeof(val1) === "object") {
                const mismatch = this.findMismatch(val1, val2, ignore_fields);
                if (mismatch.length > 0) {
                  return mismatch;
                }
              } else if (val1 !== val2) {
                return `${val1} !== ${val2}`;
              }
            }
          }
        }
        // If it gets to here then they match
        return "";
      } else {
        return `List difference: [${keys1.join(", ")}] !== [${keys2.join(", ")}]`;
      }
    } catch (ex) {
      console.error(ex);
    }
    return "Error";
  }

  objectsMatch(obj1: any, obj2: any, ignore_fields: string[] = []) {
    if (obj1 === null || obj1 === undefined || obj2 === null || obj2 === undefined) {
      if ((obj1 === null || obj1 === undefined) && (obj2 === null || obj2 === undefined)) {
        return true;
      } else {
        return false;
      }
    }
    try {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      let listMismatches = this.findListMismatches(keys1, keys2);
      let listsMatch = listMismatches.length === 0;
      if (!listsMatch) {
        // Check if matches other than empty
        listsMatch = true;
        for (const key of listMismatches) {
          if ((obj1[key] !== null && obj1[key] !== undefined) || (obj2[key] !== null && obj2[key] !== undefined)) {
            listsMatch = false;
            break;
          }
        }
      }
      if (listsMatch) {
        for (const key of keys1) {
          if (!ignore_fields.includes(key)) {
            const val1 = obj1[key];
            const val2 = obj2[key];
            if (typeof(val1) !== typeof(val2)) {
              return false;
            } else {
              if (typeof(val1) === "object") {
                if (!this.objectsMatch(val1, val2, ignore_fields)) {
                  return false;
                }
              } else if (val1 !== val2) {
                return false;
              }
            }
          }
        }
        // If it gets to here then they match
        return true;
      } else {
        return false;
      }
    } catch (ex) {
      console.error(ex);
    }
    return false;
  }

  objectsMatchLight(obj1: any, obj2: any, ignore_fields: string[] = []) {
    if (obj1 instanceof UpsertableBase && obj2 instanceof UpsertableBase) {
      return obj1._id === obj2._id;
    }
    if (obj1 === null || obj1 === undefined || obj2 === null || obj2 === undefined) {
      if ((obj1 === null || obj1 === undefined) && (obj2 === null || obj2 === undefined)) {
        return true;
      } else {
        return false;
      }
    }
    try {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      let listMismatches = this.findListMismatches(keys1, keys2);
      let listsMatch = listMismatches.length === 0;
      if (!listsMatch) {
        // Check if matches other than empty
        listsMatch = true;
        for (const key of listMismatches) {
          if ((obj1[key] !== null && obj1[key] !== undefined) || (obj2[key] !== null && obj2[key] !== undefined)) {
            listsMatch = false;
            break;
          }
        }
      }
      if (listsMatch) {
        for (const key of keys1) {
          if (!ignore_fields.includes(key)) {
            const val1 = obj1[key];
            const val2 = obj2[key];
            if (typeof(val1) !== typeof(val2)) {
              return false;
            } else {
              if (typeof(val1) === "object") {
                if (!this.objectsMatchLight(val1, val2, ignore_fields)) {
                  return false;
                }
              } else if (val1 !== val2) {
                return false;
              }
            }
          }
        }
        // If it gets to here then they match
        return true;
      } else {
        return false;
      }
    } catch (ex) {
      console.error(ex);
    }
    return false;
  }

  findMismatchLight(obj1: any, obj2: any, ignore_fields: string[] = []): string {
    if (obj1 instanceof UpsertableBase && obj2 instanceof UpsertableBase) {
      if (obj1._id === obj2._id) {
        return "";
      } else {
        return `Object Mismatch: ${obj1._id} !== ${obj2._id}`;
      }
    }
    if (obj1 === null || obj1 === undefined || obj2 === null || obj2 === undefined) {
      if ((obj1 === null || obj1 === undefined) && (obj2 === null || obj2 === undefined)) {
        return "";
      } else {
        return "Null difference";
      }
    }
    try {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      let listMismatches = this.findListMismatches(keys1, keys2);
      let listsMatch = listMismatches.length === 0;
      if (!listsMatch) {
        // Check if matches other than empty
        listsMatch = true;
        for (const key of listMismatches) {
          if ((obj1[key] !== null && obj1[key] !== undefined) || (obj2[key] !== null && obj2[key] !== undefined)) {
            listsMatch = false;
            break;
          }
        }
      }
      if (listsMatch) {
        for (const key of keys1) {
          if (!ignore_fields.includes(key)) {
            const val1 = obj1[key];
            const val2 = obj2[key];
            if (typeof(val1) !== typeof(val2)) {
              return `Type mismatch ${val1} !== ${val2}`;
            } else {
              if (typeof(val1) === "object") {
                const mismatch = this.findMismatch(val1, val2, ignore_fields);
                if (mismatch.length > 0) {
                  return mismatch;
                }
              } else if (val1 !== val2) {
                return `${val1} !== ${val2}`;
              }
            }
          }
        }
        // If it gets to here then they match
        return "";
      } else {
        return `List difference: [${keys1.join(", ")}] !== [${keys2.join(", ")}]`;
      }
    } catch (ex) {
      console.error(ex);
    }
    return "Error";
  }

  list1ContainsList2(list1: string[], list2: string[]) {
    for (const item of list2) {
      if (!list1.includes(item)) {
        return false;
      }
    }
    return true;
  }

  size_map: { [size_num: number]: string } = {
    1: "Tiny",
    2: "Small",
    3: "Medium",
    4: "Large",
    5: "Huge",
    6: "Gargantuan",
    7: "Size from Subrace",
    8: "Size from Race",
  }

  size_to_num_map: { [size: string]: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 } = {
    "Tiny": 1,
    "Small": 2,
    "Medium": 3,
    "Large": 4,
    "Huge": 5,
    "Gargantuan": 6,
    "Size from Subrace": 7,
    "Size from Race": 8
  }

  cr_to_xp_map: any = {
    0: 10,
    0.125: 25,
    0.25: 50,
    0.5: 100,
    1: 200,
    2: 450,
    3: 700,
    4: 1100,
    5: 1800,
    6: 2300,
    7: 2900,
    8: 3900,
    9: 5000,
    10: 5900,
    11: 7200,
    12: 8400,
    13: 10000,
    14: 11500,
    15: 13000,
    16: 15000,
    17: 18000,
    18: 20000,
    19: 22000,
    20: 25000,
    21: 33000,
    22: 41000,
    23: 50000,
    24: 62000,
    25: 75000,
    26: 90000,
    27: 105000,
    28: 120000,
    29: 135000,
    30: 155000
  }

  level_to_xp_thresholds: { [level: number]: { easy: number, medium: number, hard: number, deadly: number }} = {
    1: { easy: 25, medium: 50, hard: 75, deadly: 100 },
    2: { easy: 50, medium: 100, hard: 150, deadly: 200 },
    3: { easy: 75, medium: 150, hard: 225, deadly: 400 },
    4: { easy: 125, medium: 250, hard: 375, deadly: 500 },
    5: { easy: 250, medium: 500, hard: 750, deadly: 1100 },
    6: { easy: 300, medium: 600, hard: 900, deadly: 1400 },
    7: { easy: 350, medium: 750, hard: 1100, deadly: 1700 },
    8: { easy: 450, medium: 900, hard: 1400, deadly: 2100 },
    9: { easy: 550, medium: 1100, hard: 1600, deadly: 2400 },
    10: { easy: 600, medium: 1200, hard: 1900, deadly: 2800 },
    11: { easy: 800, medium: 1600, hard: 2400, deadly: 3600 },
    12: { easy: 1000, medium: 2000, hard: 3000, deadly: 4500 },
    13: { easy: 1100, medium: 2200, hard: 3400, deadly: 5100 },
    14: { easy: 1250, medium: 2500, hard: 3800, deadly: 5700 },
    15: { easy: 1400, medium: 2800, hard: 4300, deadly: 6400 },
    16: { easy: 1600, medium: 3200, hard: 4800, deadly: 7200 },
    17: { easy: 2000, medium: 3900, hard: 5900, deadly: 8800 },
    18: { easy: 2100, medium: 4200, hard: 6300, deadly: 9500 },
    19: { easy: 2400, medium: 4900, hard: 7300, deadly: 10900 },
    20: { easy: 2800, medium: 5700, hard: 8500, deadly: 12700 }
  }

  get_size_strings(nums: number[]): string[] {
    const strs: string[] = [];
    nums.forEach(num => {
      const str = this.size_map[num];
      if (str) {
        strs.push(str);
      }
    });
    return strs;
  }

  get_size_numbers(strs: string[]): number[] {
    const nums: number[] = [];
    strs.forEach(str => {
      const num = this.size_to_num_map[str];
      if (num) {
        nums.push(num);
      }
    });
    return nums;
  }

  cyrb128(str: string) {
    let h1 = 1779033703, h2 = 3144134277,
      h3 = 1013904242, h4 = 2773480762;
    for (let i = 0, k; i < str.length; i++) {
      k = str.charCodeAt(i);
      h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
      h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
      h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
      h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
    }
    h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
    h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
    h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
    h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
    return [(h1^h2^h3^h4)>>>0, (h2^h1)>>>0, (h3^h1)>>>0, (h4^h1)>>>0];
  }

  sfc32(a: number, b: number, c: number, d: number) {
    return function() {
      a >>>= 0; b >>>= 0; c >>>= 0; d >>>= 0; 
      let t = (a + b) | 0;
      a = b ^ (b >>> 9);
      b = c + (c << 3) | 0;
      c = ((c << 21) | (c >>> 11));
      d = d + 1 | 0;
      t = t + d | 0;
      c = c + t | 0;
      return (t >>> 0) / 4294967296;
    }
  }

  mulberry32(a: number) {
    return function() {
      let t = a += 0x6D2B79F5;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }
  }

  /**
   * Converts a number to a word form, no spaces or digit names (hundred, etc)
   * @param num 
   * @returns 
   */
  numberString(num: number) {
    let str = "";
    const num_str = `${num}`;
    for (let i = 0; i < num_str.length; ++i) {
      switch(num_str[i]) {
        case "0": str += "zero"; break;
        case "1": str += "one"; break;
        case "2": str += "two"; break;
        case "3": str += "three"; break;
        case "4": str += "four"; break;
        case "5": str += "five"; break;
        case "6": str += "six"; break;
        case "7": str += "seven"; break;
        case "8": str += "eight"; break;
        case "9": str += "nine"; break;
        default: str += num_str[i]; break;
      }
    }
    return str;
  }
  numberThString(num: number) {
    if (Number.isInteger(num)) {
      let th = "th";
      const last_digit = num % 10;
      const second_to_last_digit = ((num % 100) - last_digit) / 10;
      if (second_to_last_digit === 1) {
        th = "th";
      } else {
        switch (last_digit) {
          case 1: th = "st"; break;
          case 2: th = "nd"; break;
          case 3: th = "rd"; break;
        }
      }
      return `${num}${th}`;
    } else {
      return `${num}`;
    }
  }

  proficiencyBonus(level: number): number {
    const level_div_5 = level < 1 ? 0 : Math.floor((level - 1) / 4);
    return level_div_5 + 2;
  }

  get_random_0_to_1() {
    // Create cyrb128 state:
    // const d = Date();
    // const d = Date.now();
    const now = new Date(),
    then = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,0,0),
    diff = now.getTime() - then.getTime();

    const a = this.numberString(diff + Math.ceil(Math.random() * 1000));
    const seed = this.cyrb128(a);
    // Four 32-bit component hashes provide the seed for sfc32.
    const rand1 = this.sfc32(seed[0], seed[1], seed[2], seed[3]);
    
    const go = Math.ceil(Math.random() * 100);
    for (let i = 0; i < go; ++i) {
      rand1();
    }

    // Only one 32-bit component hash is needed for mulberry32.
    // const rand2 = this.mulberry32(seed[0]);

    // Obtain sequential random numbers like so:
    return rand1();
  }

  get_random(max: number) {
    const rand = this.get_random_0_to_1();
    return Math.ceil(rand * max);
  }

  get_time_number(date: Date | null = null): number {
    if (!date) {
      date = new Date();
    }
    const num = date.getFullYear() * 10000000000 + 
      date.getMonth() * 100000000 +
      date.getDate() * 1000000 +
      date.getHours() * 10000 +
      date.getMinutes() * 100 + 
      date.getSeconds();
    
    return num;
  }

  get_date_number(date: Date | null = null): number {
    if (!date) {
      date = new Date();
    }
    const num = date.getFullYear() * 10000 + 
      date.getMonth() * 100 +
      date.getDate();
    
    return num;
  }

  time_string(time_number: number) {
    const date = new Date();
    let num = time_number;
    const year = Math.floor(num / 10000000000);
    num = num % 10000000000;
    const mon = Math.floor(num / 100000000);
    num = num % 100000000;
    const d = Math.floor(num / 1000000);
    num = num % 1000000;
    const h = Math.floor(num / 10000);
    num = num % 10000;
    const m = num;
    num = num % 100;
    const s = num;
    date.setFullYear(year);
    date.setMonth(mon);
    date.setDate(d);
    date.setHours(h);
    date.setMinutes(m);
    date.setSeconds(s);
    return date.toLocaleDateString();
  }

  genLink(obj: ModelBase, type: string) {
    return `[${type}:${obj.data_type};${obj.true_name}(${obj._id})]`;
  }

  recursive_compare_objects(obj1: any, obj2: any, log_it = false): boolean {
    const keys = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys.length !== keys2.length) {
      return false;
    } else {
      for (const key of keys) {
        if (!keys2.includes(key)) {
          return false;
        }
      }
    }
    for (let i = 0; i < keys.length; ++i) {
      const key = keys[i];
      if (!["true_id","_id","id"].includes(key) && !key.includes("feature_points") && !this.recursive_compare_values(obj1[key], obj2[key], log_it)) {
        if (log_it) {
          console.log(obj1);
          console.log(obj2);
          console.log(key);
          console.log(obj1[key]);
          console.log(obj2[key]);
        }
        return false;
      }
    }
    return true;
  }

  recursive_compare_arrays(arr1: any[], arr2: any[], log_it: boolean): boolean {
    for (let i = 0; i < arr1.length; ++i) {
      if (!this.recursive_compare_values(arr1[i], arr2[i], log_it)) {
        if (log_it) {
          console.log(arr1);
          console.log(arr2);
        }
        return false;
      }
    }
    return true;
  }

  recursive_compare_values(val1: any, val2: any, log_it: boolean): boolean {
    if (typeof(val1) === "object") {
      // Check whether it's an array.
      if(Array.isArray(val1)){
        // It's an array, so do an array compare
        if (val2 && typeof(val2) === "object" && Array.isArray(val2) && val1.length === val2.length) {
          // Also an array, and same length, so compare the items in it
          return this.recursive_compare_arrays(val1, val2, log_it);
        } else {
          if (log_it) {
            console.log(val1);
            console.log(val2);
          }
          return false;
        }
      } else if (val2 && typeof(val2) === "object") {
        // It's an object, and it's on the other object, so recurse
        return this.recursive_compare_objects(val1, val2);
      }
    } else {
      // It's not an array or an object, so we just need to compare the values
      if (val1 === val2) {
        return true;
      } else if (typeof(val1) === "string") {
        if (log_it) {
          console.log(val1);
          console.log(val2);
          console.log(val1.includes("Level"));
        }
        if (val2 === undefined || val1.trim() === "") {
          return true;
        } else if (val1.trim() === val2.trim()) {
          return true;
        } else if (val2.includes("Level")) {
          return true;
        } else {
          return false;
        }
        // return val1.includes("Level");
      } else {
        if (log_it) {
          console.log(val1);
          console.log(val2);
        }
        return false;
      }
    }
    return true; // Should never come here, but it makes lint happy
  }

  cleanSpaces(clean_me: string) {
    if (clean_me) {
      let cleaned = clean_me.replace(/[\u00A0\u202F\u2009\u2007\u2060]/g, ' ').trim();
      // changed = common_util.replaceAll(changed, "\t", " ");
      while (cleaned.includes("\t")) {
        cleaned = cleaned.replace("\t", " ");
      }
      // changed = common_util.replaceAll(changed, "  ", " ");
      while (cleaned.includes("  ")) {
        cleaned = cleaned.replace("  ", " ");
      }
      return cleaned;
    } else if (clean_me.length > 0) {
      console.error("Invalid clean_me: " + clean_me);
      debugger;
    }
    return "";
  }

  cleanSpacesForElement(clean_me: HTMLElement) {
    return this.cleanSpaces(this.extractTextContent(clean_me));
  }

  extractTextContent(element: HTMLElement): string {
    const parts: string[] = [];
  
    function traverse(node: Node): void {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) parts.push(text);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        node.childNodes.forEach(traverse);
      }
    }
  
    traverse(element);
    return parts.join(' ');
  }

  cleanLineBreaks(cleanMe: string) {
    let str = cleanMe;
    while(str.includes("\n")) {
      str = str.replace("\n","<br/>");
    }
    while(str.includes("<table><br/>")) {
      str = str.replace("<table><br/>","<table>");
    }
    while(str.includes("<tbody><br/>")) {
      str = str.replace("<tbody><br/>","<tbody>");
    }
    while(str.includes("<thead><br/>")) {
      str = str.replace("<thead><br/>","<thead>");
    }
    while(str.includes("<tr><br/>")) {
      str = str.replace("<tr><br/>","<tr>");
    }
    while(str.includes("<th><br/>")) {
      str = str.replace("<th><br/>","<th>");
    }
    while(str.includes("<td><br/>")) {
      str = str.replace("<td><br/>","<td>");
    }
    // while(str.includes("</table><br/>")) {
    //   str = str.replace("</table><br/>","</table>");
    // }
    while(str.includes("</tbody><br/>")) {
      str = str.replace("</tbody><br/>","</tbody>");
    }
    while(str.includes("</thead><br/>")) {
      str = str.replace("</thead><br/>","</thead>");
    }
    while(str.includes("</tr><br/>")) {
      str = str.replace("</tr><br/>","</tr>");
    }
    while(str.includes("</th><br/>")) {
      str = str.replace("</th><br/>","</th>");
    }
    while(str.includes("</td><br/>")) {
      str = str.replace("</td><br/>","</td>");
    }
    while(str.includes("/n")) {
      str = str.replace("/n","");
    }
    return str;
  }

  removeLineBreaks(str: string): string {
    return str
      .replace(/\r\n/g, " ")
      .replace(/\r/g, " ")
      .replace(/\n/g, " ")
      .replace(/\u2028/g, " ")
      .replace(/\u2029/g, " ")
      .trim();
  }
  // Bold Me. Text that is giving me a line break when
  // it shouldn't. It can be pretty frustrating, but 
  // I think we can get past it.
  // Bold Me Too. More text that is giving me line
  // breaks when it shouldn't.
  cleanDescription(str: string) {
    return this.normalizeDashes(this.cleanLineBreaks2(str));
  }
  cleanLineBreaks2(text: string): string {
    const lines = text.split('\n');
    const result: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const nextLine = lines[i + 1];
      if (nextLine && nextLine.includes("•")) {
        console.log(line);
      }

      if (nextLine === undefined) {
        result.push(this.applyBoldHeader(line.trimEnd()));
        continue;
      }

      const trimmedLine = line.trimEnd();
      const trimmedNext = nextLine.trimStart();

      const endsWithPeriod = /\.\s*$/.test(trimmedLine);
      const nextIsNewEntry = /^[A-Z][^.]+\./.test(trimmedNext);

      if (endsWithPeriod || nextIsNewEntry) {
        result.push(this.applyBoldHeader(trimmedLine));
      } else {
        if (trimmedNext.startsWith("•")) {
          lines[i + 1] = trimmedLine + "\n" + trimmedNext;
        } else {
          lines[i + 1] = trimmedLine + " " + trimmedNext;
        }
      }
    }

    // Join with double line breaks, then collapse any runs of 3+ newlines to 2
    return result
      .join('\n\n')
      .replace(/\n{3,}/g, '\n\n');
  }
  applyBoldHeader(line: string): string {
    // Match a short phrase (1-4 words) at the start of the line followed by a period
    // Fix "1.Word" -> "1. Word" before processing
    const normalized = line.trimStart().replace(/^(\d+\.)(\S)/, '$1 $2');

    const match = normalized.match(/^((?:\S+\s+){0,3}\S+\.)(\s+.*)?$/);
    if (!match) return normalized;

    const candidate = match[1];
    const rest = match[2] ?? '';
    const wordCount = candidate.replace(/\.$/, '').trim().split(/\s+/).length;

    if (wordCount <= 4 || candidate === "Using a Higher-Level Spell Slot.") {
      return `<b>${candidate}</b>${rest}`;
    }

    return normalized;
  }
  normalizeDashes(text: string): string {
    return this.replaceAll(this.replaceAll(this.replaceAll(this.replaceAll(text
      .replace(/[-\u00AD]+-\s*/g, "-")  // collapse dash+softhyphen+dash (with optional space) to single dash
      .replace(DASH_VARIANTS, "-"), "-­-","-"), "-­-","-"), "—", "-"), "- ", "-");
  }
  normalizePotenceStrings(input: string, log_it: boolean = false): string {
    const changed = input.replace(
      /\{(potence_string(?:\.(?:\$\{[^}]+\}|[^}.]+))+)\.\{cfs\.current_ability_scores\.([^}]+)\}\}/g,
      '{$1.$2}'
    );
    if (log_it) {
      // console.log("input: ", input);
      // console.log("changed:", changed);
    }
    return changed;
  }
  normalizeApostrophes(str: string) {
    return this.replaceAll(str, "’", "'");
  }
  normalizeSpaces(input: string): string {
    return input
      // HTML entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&ensp;/g, ' ')
      .replace(/&emsp;/g, ' ')
      .replace(/&thinsp;/g, ' ')
      // Unicode space characters
      .replace(/\u00A0/g, ' ') // Non-breaking space
      .replace(/\u2002/g, ' ') // En space
      .replace(/\u2003/g, ' ') // Em space
      .replace(/\u2004/g, ' ') // Three-per-em space
      .replace(/\u2005/g, ' ') // Four-per-em space
      .replace(/\u2006/g, ' ') // Six-per-em space
      .replace(/\u2007/g, ' ') // Figure space
      .replace(/\u2008/g, ' ') // Punctuation space
      .replace(/\u2009/g, ' ') // Thin space
      .replace(/\u200A/g, ' ') // Hair space
      .replace(/\u202F/g, ' ') // Narrow no-break space
      .replace(/\u205F/g, ' ') // Medium mathematical space
      .replace(/\u3000/g, ' ') // Ideographic space
      .replace(/\uFEFF/g, ' ') // Zero-width no-break space (BOM)
      // Zero-width characters (replace with empty string)
      .replace(/\u200B/g, '')  // Zero-width space
      .replace(/\u200C/g, '')  // Zero-width non-joiner
      .replace(/\u200D/g, '');  // Zero-width joiner
  }
  cleanForCompare(str: string, log_it: boolean = false) {
    return this.normalizeSpaces(this.normalizeApostrophes(this.normalizePotenceStrings(this.normalizeDashes(this.removeLineBreaks(this.cleanSpaces(str)).toLocaleLowerCase()), log_it)));
  }
  cleanCompare(str1: string, str2: string, log_it: boolean = false) {
    const compareMe1 = this.cleanForCompare(str1, log_it);
    const compareMe2 = this.cleanForCompare(str2, log_it);
    if (log_it) {
      console.log("compareMe1: ", compareMe1);
      console.log("compareMe2: ", compareMe2);
    }
    return compareMe1 === compareMe2;
  }

  statAbbreviation(score: string): string {
    let abbreviation = "";
    switch(score.toLowerCase().trim()) {
      case "str":
        abbreviation = "STR";
      break;
      case "strength":
        abbreviation = "STR";
      break;
      case "dex":
        abbreviation = "DEX";
      break;
      case "dexterity":
        abbreviation = "DEX";
      break;
      case "con":
        abbreviation = "CON";
      break;
      case "constitution":
        abbreviation = "CON";
      break;
      case "int":
        abbreviation = "INT";
      break;
      case "intelligence":
        abbreviation = "INT";
      break;
      case "wis":
        abbreviation = "WIS";
      break;
      case "wisdom":
        abbreviation = "WIS";
      break;
      case "cha":
        abbreviation = "CHA";
      break;
      case "charisma":
        abbreviation = "CHA";
      break;
    }
    return abbreviation;
  }

  statFull(abbreviation: string): string {
    let score = "";
    switch(abbreviation) {
      case "STR":
        score = "Strength";
      break;
      case "Strength":
        score = "Strength";
      break;
      case "DEX":
        score = "Dexterity";
      break;
      case "Dexterity":
        score = "Dexterity";
      break;
      case "CON":
        score = "Constitution";
      break;
      case "Constitution":
        score = "Constitution";
      break;
      case "INT":
        score = "Intelligence";
      break;
      case "Intelligence":
        score = "Intelligence";
      break;
      case "WIS":
        score = "Wisdom";
      break;
      case "Wisdom":
        score = "Wisdom";
      break;
      case "CHA":
        score = "Charisma";
      break;
      case "Charisma":
        score = "Charisma";
      break;
    }
    return score;
  }

  parseDiceRoll(str: string): RollPlus {
    const dice_roll = new RollPlus();
    // 2d4+4
    // 2d4 + 4
    // 2d4-1
    // 2d4 - 1
    if (str.includes("+")) {
      dice_roll.flat = this.fix_number_string(str.substring(str.indexOf("+")));
      str = str.substring(0, str.indexOf("+") - 1).trim();
    } else if (str.includes("-")) {
      dice_roll.flat = this.fix_number_string(str.substring(str.indexOf("-")));
      if (dice_roll.flat > 0) {
        dice_roll.flat *= -1;
      }
      str = str.substring(0, str.indexOf("-") - 1).trim();
    } 
    if (str.includes("d")) {
      const pieces = str.split("d");
      if (pieces.length === 2) {
        dice_roll.count = this.fix_number_string(pieces[0]);
        dice_roll.size = this.fix_number_string(pieces[1]);
      }
    } else {
      dice_roll.flat = this.fix_number_string(str);
    }
    return dice_roll;
  }

  parseDiceRollUpgradable(str: string) {
    const dice_roll = new RollPlusUpgradable();
    // 2d4 plus your spellcasting ability modifier
    // 2d4+4
    // 2d4 + 4
    // 2d4-1
    // 2d4 - 1
    
    const flat_from_string = (flat_str: string) => {
      if (flat_str.includes("spellcasting")) {
        // dice_roll.flat.add_ability_mod_mult = 1;
        // dice_roll.flat.add_ability_scores.push("Spellcasting");
        dice_roll.flat.add_stats_groups = [{ 
          true_id: uuidv4().toString(),
          add_ability_scores: ["Spellcasting"],
          mod_or_score: "Mod",
          add_mult: 1,
          multiple_type: "And"
        }];
      } else {
        if (flat_str.includes("strength")) {
          // dice_roll.flat.add_ability_mod_mult = 1;
          // dice_roll.flat.add_ability_scores.push("STR");
          dice_roll.flat.add_stats_groups = [{ 
            true_id: uuidv4().toString(),
            add_ability_scores: ["STR"],
            mod_or_score: "Mod",
            add_mult: 1,
            multiple_type: "And"
          }];
        } else if (flat_str.includes("dexterity")) {
          // dice_roll.flat.add_ability_mod_mult = 1;
          // dice_roll.flat.add_ability_scores.push("DEX");
          dice_roll.flat.add_stats_groups = [{ 
            true_id: uuidv4().toString(),
            add_ability_scores: ["DEX"],
            mod_or_score: "Mod",
            add_mult: 1,
            multiple_type: "And"
          }];
        } else if (flat_str.includes("constitution")) {
          // dice_roll.flat.add_ability_mod_mult = 1;
          // dice_roll.flat.add_ability_scores.push("CON");
          dice_roll.flat.add_stats_groups = [{ 
            true_id: uuidv4().toString(),
            add_ability_scores: ["CON"],
            mod_or_score: "Mod",
            add_mult: 1,
            multiple_type: "And"
          }];
        } else if (flat_str.includes("intelligence")) {
          // dice_roll.flat.add_ability_mod_mult = 1;
          // dice_roll.flat.add_ability_scores.push("INT");
          dice_roll.flat.add_stats_groups = [{ 
            true_id: uuidv4().toString(),
            add_ability_scores: ["INT"],
            mod_or_score: "Mod",
            add_mult: 1,
            multiple_type: "And"
          }];
        } else if (flat_str.includes("wisdom")) {
          // dice_roll.flat.add_ability_mod_mult = 1;
          // dice_roll.flat.add_ability_scores.push("WIS");
          dice_roll.flat.add_stats_groups = [{ 
            true_id: uuidv4().toString(),
            add_ability_scores: ["WIS"],
            mod_or_score: "Mod",
            add_mult: 1,
            multiple_type: "And"
          }];
        } else if (flat_str.includes("charisma")) {
          // dice_roll.flat.add_ability_mod_mult = 1;
          // dice_roll.flat.add_ability_scores.push("CHA");
          dice_roll.flat.add_stats_groups = [{ 
            true_id: uuidv4().toString(),
            add_ability_scores: ["CHA"],
            mod_or_score: "Mod",
            add_mult: 1,
            multiple_type: "And"
          }];
        } else {
          let flat = +flat_str;
          if (!isNaN(flat)) {
            dice_roll.flat.base = flat;
          } else {
            const words = flat_str.split(" ");
            for (const word of words) {
              flat = +word;
              if (!isNaN(flat)) {
                dice_roll.flat.base = flat;
                break;
              }
            }
          }
        }
      }
    }

    if (str.includes("+")) {
      const flat_str = str.substring(str.indexOf("+")).toLocaleLowerCase();
      flat_from_string(flat_str);
      str = str.substring(0, str.indexOf("+") - 1).trim();
    } else if (str.includes("-")) {
      dice_roll.flat.base = this.fix_number_string(str.substring(str.indexOf("-")));
      if (dice_roll.flat.base > 0) {
        dice_roll.flat.base *= -1;
      }
      str = str.substring(0, str.indexOf("-") - 1).trim();
    } else if (str.includes("plus")) {
      const flat_str = str.substring(str.indexOf("plus") + 4).toLocaleLowerCase();
      flat_from_string(flat_str);
      str = str.substring(0, str.indexOf("plus") - 1).trim();
    } 
    const pieces = str.split("d");
    if (pieces.length === 2) {
      const count = this.fix_number_string(pieces[0]);
      const size = this.fix_number_string(pieces[1]);
      if (count > 0 && size > 0) {
        dice_roll.count.base = count;
        dice_roll.size.base = size;
      } else {
        flat_from_string(str);
      }
    } else if (dice_roll.flat.isBlank()) {
      flat_from_string(str);
    }
    return dice_roll;
  }
 
  /**
   * Strips all attributes from HTML elements while preserving the structure and content
   * @param html - The HTML string to process
   * @returns The HTML string with all attributes removed
   */
  stripHtmlAttributes(elem: HTMLElement) {
    // Create a DOMParser instance
    const parser = new DOMParser();
    const doc = parser.parseFromString(elem.outerHTML, 'text/html');

    // Recursive function to process nodes
    const processNode = (node: Node) =>{
      // If it's an element, remove all attributes
      if (node instanceof Element) {
        // Get the tag name
        const tagName = node.tagName.toLowerCase();
      
        // Create a new element with the same tag name
        const newElement = document.createElement(tagName);
      
        // Copy over the children
        while (node.firstChild) {
          processNode(node.firstChild);
          newElement.appendChild(node.firstChild);
        }
      
        // Replace the old element with the new one
        node.parentNode?.replaceChild(newElement, node);
      }
    
      // Process child nodes
      for (const child of Array.from(node.childNodes)) {
        processNode(child);
      }
    }

    // Process the body
    processNode(doc.body);

    // Return the processed HTML (excluding body tags)
    return doc.body.innerHTML;
  }

  getSpellWithOverridableCostFromSFLWC(spell: Spell, sflwc: SpellsFromListWithCosts) {
    let s = sflwc.spells.find(o => o.spell_id === spell._id);
    if (!s) {
      // It probably couldn't find it because the feature was made with a now non-dominant spell.
      const variant_ids = spell.variant_ids;
      s = sflwc.spells.find(o => variant_ids.includes(o.spell_id));
    }
    return s;
  }
  
  hashPassword = async (password: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  }
  
  // const items = ['Apples', 'Oranges', 'Bananas'];
  // console.log(customJoin(items, ', ', ' and ')); // "Apples, Oranges and Bananas"
  standardJoin(arr: string[], and_or: "and" | "or" = "or") {
    return this.customJoin({ arr, separator: ", ", lastSeparator: `, ${and_or} `, onlySeparator: ` ${and_or} `});
  }
  
  // const items = ['Apples', 'Oranges', 'Bananas'];
  // console.log(customJoin(items, ', ', ' and ')); // "Apples, Oranges and Bananas"
  customJoin({ arr, separator, lastSeparator, onlySeparator }: { arr: string[], separator: string, lastSeparator: string, onlySeparator?: string }) {
    if (arr.length <= 1) return arr.join('');
    if (arr.length === 2 && onlySeparator) {
      return arr[0] + onlySeparator + arr[1];
    }
    const last = arr.pop(); // Or use arr[arr.length - 1] to avoid mutating
    return arr.join(separator) + lastSeparator + last;
  }

  commatizeNumber(num: number) {
    if (num) {
      return num.toLocaleString('en-US');
    }
    return "0";
  }
  
  modFromScore = (score: number) => {
    return Math.floor((score - 10) / 2);
  }
  
  makeStringLinkReady = (str: string): string => {
    const remove_us = ["/",":","'","-"];
    let fixed = this.replaceAll(str.toLocaleLowerCase(), " ", "_");
    for (const remove_me of remove_us) {
      fixed = this.replaceAll(fixed, remove_me, "");
    }
    return fixed;
  };

  removeTagAttributes = (str: string) => {
    const unwrappedHtml = str.replace(/<a\b[^>]*>(.*?)<\/a>/gi, '$1');
    return unwrappedHtml.replace(/<(\w+)\s*[^>]*?(\/?)>/g, (_match, tagName, selfClose) => {
      return `<${tagName}${selfClose}>`;
    });
  };
}

